import React, { useState, useEffect } from "react";
import { ImagePlus, Loader2, Sparkles, Download } from "lucide-react";
import { aiImage, listImages } from "../api";

export default function ImageGen() {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => { listImages().then(setImages).catch(() => {}); }, []);

  const gen = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await aiImage(prompt);
      setImages(prev => [{ id: r.id, prompt, data_url: r.data_url, ts: Date.now() }, ...prev]);
      setPrompt("");
    } catch (e) {
      setErr("Generation failed. Try a different prompt.");
    }
    setBusy(false);
  };

  return (
    <div className="h-full flex flex-col" data-testid="app-imagegen">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} color="#ff7a2e" />
          <span className="text-sm font-medium">AI Image Studio</span>
          <span className="text-[10px] text-white/40 mono">nano-banana</span>
        </div>
        <div className="flex gap-2">
          <textarea
            data-testid="image-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), gen())}
            placeholder="A cinematic shot of a neon-lit Tokyo alley in the rain..."
            rows={2}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-white/30 resize-none"
          />
          <button
            data-testid="image-generate"
            onClick={gen}
            disabled={busy}
            className="btn-accent flex items-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
            Generate
          </button>
        </div>
        {err && <div className="text-xs text-red-400 mt-2">{err}</div>}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {images.length === 0 ? (
          <div className="text-center mt-20 text-white/40 text-sm">
            <ImagePlus size={32} className="mx-auto mb-3" />
            Your generations will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {images.map((img) => (
              <div key={img.id} className="rounded-lg overflow-hidden border border-white/10 bg-black/40 group relative">
                <img src={img.data_url} alt={img.prompt} className="w-full h-auto" />
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition">
                  <div className="text-[11px] text-white/80 line-clamp-2">{img.prompt}</div>
                  <a
                    href={img.data_url}
                    download={`hexa-image-${img.id}.png`}
                    className="inline-flex items-center gap-1 text-[10px] text-[#ff7a2e] mt-1"
                  >
                    <Download size={10} /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
